import type {
  Artifact as SyncConfigArtifact,
  Channel,
  GetAllArtifactsGroupedResponse,
  ListTrainsResponse,
  SourceDeploymentConfig,
  SyncEngineDeploymentInfo,
  Train as SyncConfigTrain,
  VersionInfo,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';

// The Sync Config API allows arbitrary channel names.
export type ChannelName = NonNullable<Channel['channelName']>;

export const ChannelName = {
  ALPHA: 'alpha',
  BETA: 'beta',
  STABLE: 'stable',
} as const;

export type SyncConfigChannelResponse = Channel & {
  channel_name?: string;
  current_train_id?: string;
  updated_at?: string;
  channel_promoted_by?: string;
};

export type SyncConfigArtifactResponse = SyncConfigArtifact & {
  artifact_id?: string;
  package_name?: string;
  version_tag?: string;
  target_os?: SyncConfigArtifact['targetOs'];
  target_arch?: SyncConfigArtifact['targetArch'];
  target_pms?: string;
  gcs_artifact_url?: string;
  sha256_checksum?: string;
  created_at?: string;
};

export type SyncConfigTrainResponse = Omit<SyncConfigTrain, 'artifacts'> & {
  train_id?: string;
  train_name?: string;
  created_by?: string;
  created_at?: string;
  promoted_channel?: string;
  channel_promoted_by?: string;
  artifacts?: SyncConfigArtifactResponse[];
};

export type SyncConfigListTrainsResponse = ListTrainsResponse & {
  total_count?: number;
};

export type SyncConfigGroupedArtifactsResponse = GetAllArtifactsGroupedResponse & {
  total_count?: number;
};

export type SyncConfigVersionInfoResponse = VersionInfo & {
  binary_name?: string;
  expected_signature?: string;
  target_version?: string;
  target_channel?: string;
  package_name?: string;
  running_version?: string;
};

export type SyncConfigEngineDeploymentResponse = Omit<SyncEngineDeploymentInfo, 'versionInfo'> & {
  sync_engine_id?: string;
  versionInfo?: SyncConfigVersionInfoResponse;
  version_info?: SyncConfigVersionInfoResponse;
};

export type SyncConfigSourceDeploymentResponse = Omit<
  SourceDeploymentConfig,
  'syncEngines' | 'containerVersionInfo'
> & {
  source_id?: string;
  syncEngines?: SyncConfigEngineDeploymentResponse[];
  sync_engines?: SyncConfigEngineDeploymentResponse[];
  containerVersionInfo?: SyncConfigVersionInfoResponse;
  container_version_info?: SyncConfigVersionInfoResponse;
  target_channel?: string;
  campaign_override_id?: string;
  target_train_id?: string;
};

export enum EngineOverrideKind {
  ActivePin = 'active-pin',
  SourcePolicy = 'source-policy',
}

export type EngineOverride =
  | { kind: EngineOverrideKind.ActivePin; version: string }
  | { kind: EngineOverrideKind.SourcePolicy; label: 'Follows Train' | 'Follows Channel' | 'N/A' };

export interface SourceDeploymentConfigUpdate {
  targetChannel?: string;
  targetTrainId?: string;
  enginePin?: {
    engine: SyncEngineDeploymentInfo;
    targetVersion: string;
  };
}

export type VersionsByPackage = Map<string, Set<string>>;
